<template>
  <div>
    <v-btn
      v-show="isDev"
      icon
      dark
      @click="open = true"
      title="Ver logs de conexión"
    >
      <v-badge
        :content="unreadCount"
        :value="unreadCount > 0"
        color="red"
        overlap
      >
        <v-icon>mdi-console</v-icon>
      </v-badge>
    </v-btn>

    <v-dialog v-model="open" max-width="740" scrollable>
      <v-card dark color="#1e1e1e">
        <v-card-title class="d-flex justify-space-between align-center pa-3" style="background:#2b2b2b">
          <span style="font-size:14px; font-family: monospace;">
            <v-icon small class="mr-1">mdi-console</v-icon>
            Socket Logs
          </span>
          <div class="d-flex align-center" style="gap:4px">
            <v-btn
              x-small
              depressed
              :color="connected ? 'green darken-1' : 'grey darken-2'"
              class="mr-2"
              style="font-size:10px"
              @click="toggleConnection"
              :title="connected ? 'Desconectar socket' : 'Conectar socket'"
            >
              <v-icon x-small left>{{ connected ? 'mdi-lan-connect' : 'mdi-lan-disconnect' }}</v-icon>
              {{ connected ? 'Conectado' : 'Desconectado' }}
            </v-btn>
            <v-btn-toggle v-model="filter" dense mandatory color="white" style="background:transparent">
              <v-btn x-small value="all" text style="font-size:10px">Todo</v-btn>
              <v-btn x-small value="connection" text style="font-size:10px">Conexión</v-btn>
              <v-btn x-small value="event" text style="font-size:10px">Eventos</v-btn>
            </v-btn-toggle>
            <v-btn icon x-small @click="clearLogs" title="Limpiar">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
            <v-btn icon x-small @click="open = false">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-divider />

        <v-card-text
          ref="logContainer"
          class="pa-2"
          style="height: 420px; overflow-y: auto; font-family: monospace; font-size: 12px; background: #1e1e1e;"
        >
          <div v-if="filteredLogs.length === 0" style="color: #666; padding: 8px;">
            Sin logs aún...
          </div>
          <div
            v-for="(log, i) in filteredLogs"
            :key="i"
            class="log-line"
          >
            <span class="log-time">{{ log.time }}</span>
            <span class="log-badge" :class="log.level">{{ log.level }}</span>
            <span class="log-event" v-if="log.event">{{ log.event }}</span>
            <span class="log-msg">{{ log.message }}</span>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions style="background:#2b2b2b; padding: 4px 8px;">
          <span style="font-size:11px; color:#888; font-family:monospace;">
            {{ filteredLogs.length }} / {{ logs.length }} entrada(s)
          </span>
          <v-spacer />
          <v-btn x-small text color="grey" @click="scrollToBottom">
            <v-icon x-small class="mr-1">mdi-arrow-down</v-icon>
            Ir al final
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { socketAutoConnectDisabled } from '@/socket'

export default {
  name: 'LogsTab',
  data() {
    return {
      open: false,
      logs: [],
      unreadCount: 0,
      filter: 'all',
      handlers: {},
      connected: false,
      autoConnectDisabled: socketAutoConnectDisabled,
    }
  },
  computed: {
    ...mapState(['studio']),
    isDev() {
      return process.env.NODE_ENV === 'development'
    },
    filteredLogs() {
      if (this.filter === 'all') return this.logs
      if (this.filter === 'connection') return this.logs.filter(l => l.category === 'connection')
      if (this.filter === 'event') return this.logs.filter(l => l.category === 'event')
      return this.logs
    }
  },
  watch: {
    open(val) {
      if (val) {
        this.unreadCount = 0
        this.$nextTick(this.scrollToBottom)
      }
    },
    studio(val) {
      if (this.autoConnectDisabled) return
      if (val != null) {
        const socket = this.$globalData.socket
        if (!socket.connected) socket.connect()
      }
    }
  },
  methods: {
    toggleConnection() {
      const socket = this.$globalData.socket
      if (socket.connected) {
        this.addLog('warn', 'Desconexión manual solicitada')
        socket.disconnect()
      } else {
        this.addLog('info', 'Conexión manual solicitada')
        socket.connect()
      }
    },
    addLog(level, message, { event = null, category = 'connection' } = {}) {
      const time = new Date().toTimeString().slice(0, 8)
      this.logs.push({ level, message, event, category, time })
      if (!this.open) this.unreadCount++
      this.$nextTick(this.scrollToBottom)
    },
    clearLogs() {
      this.logs = []
      this.unreadCount = 0
    },
    scrollToBottom() {
      const el = this.$refs.logContainer
      if (el) el.scrollTop = el.scrollHeight
    },
    formatValue(value) {
      if (value === undefined || value === null) return ''
      try { return JSON.stringify(value) } catch { return String(value) }
    },
  },
  mounted() {

    const socket = this.$globalData.socket
    this.connected = socket.connected

    if (this.autoConnectDisabled) {
      this.addLog('warn', 'Conexión automática deshabilitada (modo desarrollo). Conéctate manualmente con el botón.')
    } else if (this.studio != null && !socket.connected) {
      socket.connect()
    } else if (socket.connected) {
      this.addLog('success', `Ya conectado. ID: ${socket.id}`)
    } else if (this.studio == null) {
      this.addLog('warn', 'Sin estudio asignado. La conexión se establecerá al seleccionar un estudio.')
    }

    // --- Eventos de conexión ---
    const h = this.handlers

    h.connect = () => { this.connected = true; this.addLog('success', `Conectado. ID: ${socket.id}`) }
    h.disconnect = (reason) => { this.connected = false; this.addLog('error', `Desconectado: ${reason}`) }
    h.connect_error = (err) => this.addLog('error', `Error de conexión: ${err.message}`)
    h.reconnect = (n) => this.addLog('info', `Reconectado tras ${n} intento(s)`)
    h.reconnect_attempt = (n) => this.addLog('warn', `Intento de reconexión #${n}`)
    h.reconnect_error = (err) => this.addLog('error', `Error al reconectar: ${err.message}`)
    h.reconnect_failed = () => this.addLog('error', 'Reconexión fallida definitivamente')

    // --- Eventos de aplicación (socketPlugin) ---
    h.setConnetTechnician = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'setConnetTechnician', category: 'event' })

    h.setUsersConnected = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'setUsersConnected', category: 'event' })

    h.setAllowCitation = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'setAllowCitation', category: 'event' })

    h.textRecording = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'textRecording', category: 'event' })

    h.langIdSpot = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'langIdSpot', category: 'event' })

    h.saveDataSpot = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'saveDataSpot', category: 'event' })

    h.textChangedBy = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'textChangedBy', category: 'event' })

    h.sliderFont = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'sliderFont', category: 'event' })

    h.statusShowingRecorder = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'statusShowingRecorder', category: 'event' })

    h.updateSpotList = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'updateSpotList', category: 'event' })

    h.pendingCreativeAccessRequest = (v) =>
      this.addLog('warn', this.formatValue(v), { event: 'pendingCreativeAccessRequest', category: 'event' })

    h.joinApproved = (v) =>
      this.addLog('success', this.formatValue(v), { event: 'joinApproved', category: 'event' })

    h.joinDenied = (v) =>
      this.addLog('error', this.formatValue(v), { event: 'joinDenied', category: 'event' })

    h.kicked = (v) =>
      this.addLog('error', this.formatValue(v), { event: 'kicked', category: 'event' })

    h.spotData = (v) =>
      this.addLog('info', this.formatValue(v), { event: 'spotData', category: 'event' })

    Object.entries(h).forEach(([event, fn]) => socket.on(event, fn))
  },
  beforeDestroy() {
    const socket = this.$globalData.socket
    Object.entries(this.handlers).forEach(([event, fn]) => socket.off(event, fn))
  }
}
</script>

<style scoped>
.log-line {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 2px 4px;
  border-radius: 3px;
  line-height: 1.6;
}
.log-line:hover {
  background: #2a2a2a;
}

.log-time {
  color: #555;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-badge {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: bold;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}
.log-badge.success { background: #2d5a27; color: #7ec86b; }
.log-badge.error   { background: #5a2727; color: #e07070; }
.log-badge.warn    { background: #5a4d27; color: #e0c070; }
.log-badge.info    { background: #27455a; color: #70b0e0; }

.log-event {
  color: #c792ea;
  white-space: nowrap;
  flex-shrink: 0;
}

.log-msg {
  color: #d4d4d4;
  word-break: break-word;
}
</style>
